































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import moment from 'moment-timezone';
import { GroupLevelAttributeValue } from '@/jbi-shared/types/jaas-group-level-attributes.types';
import { FormattedTimezone } from '@/store/modules/jtms/course/course.types';
import DraggableIcon from './DraggableIcon.vue';
import AttributeActionButtons from './AttributeActionButtons.vue';

@Component({
  components: {
    DraggableIcon,
    AttributeActionButtons
  }
})
export default class GroupLevelAttributeTimezoneField extends Vue {
  @Prop() attributeValue!: GroupLevelAttributeValue;
  @Prop() fieldClasses!: any;
  @Prop() duplicationError!: boolean;
  @Prop({ default: false }) overrideDisableDraggable!: boolean;
  @Prop({ default: false }) overrideDisableAttributeButton!: boolean;
  @Prop() deleteAttribute!: () => void;
  @Prop() updateAttributeValue!: (attribute: GroupLevelAttributeValue) => void;
  @Prop() toggleAttributeRequire!: (
    attribute: GroupLevelAttributeValue
  ) => void;
  @Prop() editAttribute!: (attribute: GroupLevelAttributeValue) => void;

  fieldErrorMsg: string = '';
  selectedTimezone: FormattedTimezone = {
    display: '',
    original: ''
  };
  searchTerm: string = '';
  isActive: boolean = false;

  mounted() {
    if (
      this.attributeValue &&
      this.attributeValue.value &&
      typeof this.attributeValue.value === 'string'
    ) {
      const [display, original] = (this.attributeValue.value as string).split(
        '.'
      );
      this.selectedTimezone = {
        display,
        original
      };
    }

    // Assign current client timezone by default if nothing is selected
    if (!this.selectedTimezone.display && !this.selectedTimezone.original) {
      this.selectedTimezone = this.formatTimezones(moment.tz.guess());
    }

    this.searchTerm = this.selectedTimezone.display;
  }

  // Computed property so changes from the value from parent component are synced
  get isRequired() {
    return this.attributeValue.groupLevelAttributeSpec.isRequired;
  }

  get allFormattedTimezones(): FormattedTimezone[] {
    return moment.tz.names().map(this.formatTimezones) ?? [];
  }

  get filteredTimezones(): FormattedTimezone[] {
    if (!this.searchTerm) {
      return this.allFormattedTimezones; // Return all if no search term
    }

    const searchTermLower: string = this.searchTerm.toLocaleLowerCase();

    return this.allFormattedTimezones.filter((tzObj) => {
      const matchesOriginal = tzObj.original
        .toLocaleLowerCase()
        .includes(searchTermLower);
      const matchesDisplay = tzObj.display
        .toLocaleLowerCase()
        .includes(searchTermLower);

      return matchesOriginal || matchesDisplay;
    });
  }

  toggleIsActive() {
    this.isActive = !this.isActive;
  }

  // Helper method to modify raw timezone object with readable string label
  formatTimezones(timezone: string): FormattedTimezone {
    const now = moment.tz(timezone); // Get current time in the timezone
    const offset = now.format('Z'); // Get UTC offset (e.g., +08:00)
    const abbr = now.zoneAbbr(); // Get abbreviated zone name

    const parts = timezone.split('/');
    const simplifiedNameRaw = parts[parts.length - 1]; // Get the last segment
    const simplifiedName = simplifiedNameRaw.replace(/_/g, ' '); // Replace underscores with spaces
    const displayLabel = `${simplifiedName} (GMT ${offset})`;

    return {
      original: timezone,
      display: displayLabel
    };
  }

  handleTimezoneSelect(selection: FormattedTimezone): void {
    if (selection) {
      this.selectedTimezone = selection;
      this.searchTerm = this.selectedTimezone.display;
      return;
    }

    // Assign empty TZ
    this.selectedTimezone = {
      display: '',
      original: ''
    };
  }

  @Watch('attributeValue.value', { deep: true })
  validateRequiredFieldValues(): void {
    // validate null / empty values for required fields
    if (!this.attributeValue.value) {
      this.fieldErrorMsg = `${this.attributeValue.label} is required.`;
      this.attributeValue.hasFieldError = true;
      this.updateAttributeValue(this.attributeValue);
      return;
    }

    this.fieldErrorMsg = '';
    this.attributeValue.hasFieldError = false;
    this.updateAttributeValue(this.attributeValue);
  }

  @Watch('isRequired', { immediate: true })
  updateErrorState() {
    if (this.$attrs.isSettings) {
      this.validateRequiredFieldValues();
      return;
    }

    this.attributeValue.hasFieldError = true;
    this.updateAttributeValue(this.attributeValue);
  }

  @Watch('selectedTimezone', { deep: true })
  assignTimezoneValue() {
    if (this.selectedTimezone) {
      this.attributeValue.value = `${this.selectedTimezone.display}.${this.selectedTimezone.original}`;
    }
  }
}
